import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ChevronDown,
  LoaderCircle,
  LogOut,
  Search,
  Settings,
  User,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useApp } from "@/context/AppContext";
import { visibleAdminNavItems } from "@/lib/adminNavigation";

export default function Topbar() {
  const { currentAdmin, logout } = useApp();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const admin = currentAdmin || { name: "Admin", role: "SuperAdmin", email: "", avatar: "" };
  const navigationItems = visibleAdminNavItems(currentAdmin?.permissions);

  useEffect(() => {
    const openSearch = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen((current) => !current);
      }
    };
    window.addEventListener("keydown", openSearch);
    return () => window.removeEventListener("keydown", openSearch);
  }, []);

  const goTo = (path) => {
    navigate(path);
    setSearchOpen(false);
    setSearchQuery("");
  };

  const handleLogout = async () => {
    setIsSigningOut(true);
    try {
      await logout();
    } finally {
      navigate("/");
    }
  };

  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-30 h-16 bg-white/85 backdrop-blur-xl border-b border-[#0E5B52]/10 flex items-center px-4 md:px-6 gap-3"
    >
      <div className="flex-1 min-w-0">
        <Popover
          open={searchOpen}
          onOpenChange={(next) => {
            setSearchOpen(next);
            if (!next) setSearchQuery("");
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="console-search"
              className="group flex h-10 w-full max-w-md items-center gap-2.5 rounded-xl border border-[#0E5B52]/10 bg-[#F7FAF8] px-3 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] transition-all hover:border-[#0E5B52]/20 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E5B52]/25"
              aria-label="Search admin console"
            >
              <Search className="h-4 w-4 flex-shrink-0 text-[#0E5B52]/55 transition-colors group-hover:text-[#0E5B52]" />
              <span className="min-w-0 flex-1 truncate text-[13px] text-[#6B7280]">
                Search console…
              </span>
              <kbd className="hidden sm:inline-flex h-5 items-center rounded-md border border-[#0E5B52]/10 bg-white px-1.5 font-sans text-[10px] font-medium text-[#0E5B52]/60 shadow-sm">
                ⌘ K
              </kbd>
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={8}
            className="w-[min(36rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border-[#0E5B52]/10 p-0 shadow-[0_22px_60px_rgba(6,78,59,0.16)]"
          >
            <Command className="rounded-2xl" loop>
              <CommandInput
                autoFocus
                value={searchQuery}
                onValueChange={setSearchQuery}
                placeholder="Search pages, tools and operations…"
                aria-label="Search pages, tools and operations"
                className="h-12"
              />
              <CommandList className="max-h-[360px] p-2">
                <CommandEmpty className="py-10 text-center">
                  <Search className="mx-auto mb-2 h-5 w-5 text-[#0E5B52]/35" />
                  <div className="text-sm font-medium text-[#0F2A26]">No console page found</div>
                  <div className="mt-1 text-xs text-[#6B7280]">Try a page name or operation.</div>
                </CommandEmpty>
                <CommandGroup heading="Navigate">
                  {navigationItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <CommandItem
                        key={item.to}
                        value={`${item.label} ${item.description} ${item.keywords}`}
                        onSelect={() => goTo(item.to)}
                        className="group my-0.5 cursor-pointer rounded-xl px-3 py-2.5 data-[selected=true]:bg-[#E6F5EE]"
                      >
                        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#F0F6F1] text-[#0E5B52] group-data-[selected=true]:bg-white">
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] font-semibold text-[#0F2A26]">
                            {item.label}
                          </span>
                          <span className="block truncate text-[11px] text-[#6B7280]">
                            {item.description}
                          </span>
                        </span>
                        <ArrowRight className="h-4 w-4 text-[#0E5B52]/30 group-data-[selected=true]:text-[#0E5B52]" />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
              <div className="flex items-center justify-between border-t border-[#0E5B52]/10 bg-[#FBFDFC] px-4 py-2 text-[10px] text-[#6B7280]">
                <span>↑↓ Navigate</span>
                <span>Enter Open · Esc Close</span>
              </div>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="ml-auto flex items-center gap-2">
        {/* Admin menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="admin-profile-menu"
              className="flex items-center gap-2 h-10 pl-1 pr-2.5 rounded-md border border-[#0E5B52]/10 bg-white hover:bg-[#F0F6F1] transition-all"
            >
              <Avatar className="w-8 h-8">
                <AvatarImage src={admin.avatar || undefined} />
                <AvatarFallback className="bg-[#0E5B52] text-white text-[10px]">
                  {admin.name.split(" ").map((p) => p[0]).join("")}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:block text-left leading-tight">
                <div className="text-[13px] font-medium text-[#0F2A26]">{admin.name}</div>
                <div className="text-[10px] text-[#6B7280]">{admin.role}</div>
              </div>
              <ChevronDown className="w-3.5 h-3.5 opacity-50" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>{admin.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="admin-menu-profile" onClick={() => navigate("/admins")}>
              <User className="w-4 h-4 mr-2" /> Admin users
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="admin-menu-settings" onClick={() => navigate("/settings")}>
              <Settings className="w-4 h-4 mr-2" /> Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="admin-menu-logout"
              onSelect={() => setLogoutDialogOpen(true)}
              className="text-red-600 focus:text-red-700"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AlertDialog
        open={logoutDialogOpen}
        onOpenChange={(open) => {
          if (!isSigningOut) setLogoutDialogOpen(open);
        }}
      >
        <AlertDialogContent
          overlayClassName="bg-[#071D1A]/45 backdrop-blur-[2px]"
          className="w-[calc(100%_-_2rem)] max-w-[400px] gap-0 overflow-hidden rounded-2xl border border-[#0E5B52]/10 bg-white p-0 shadow-[0_24px_80px_rgba(15,42,38,0.2)]"
        >
          <div className="px-6 pb-5 pt-6 sm:px-7 sm:pt-7">
            <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-red-600 ring-1 ring-inset ring-red-100">
              <LogOut className="h-5 w-5" strokeWidth={2} />
            </div>
            <AlertDialogHeader className="space-y-1.5 text-left">
              <AlertDialogTitle className="text-xl font-semibold tracking-[-0.02em] text-[#0F2A26]">
                Sign out of Shopbook?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[13px] leading-5 text-[#6B7280]">
                You’ll need to sign in again to access the operations console.
              </AlertDialogDescription>
            </AlertDialogHeader>

            {admin.email && (
              <div className="mt-5 truncate rounded-lg border border-[#0E5B52]/8 bg-[#F7FAF8] px-3 py-2 text-xs font-medium text-[#35534E]">
                {admin.email}
              </div>
            )}
          </div>

          <AlertDialogFooter className="grid grid-cols-2 gap-2 border-t border-[#0E5B52]/10 bg-[#FBFDFC] px-6 py-4 sm:grid-cols-2 sm:space-x-0 sm:px-7">
            <AlertDialogCancel
              disabled={isSigningOut}
              className="mt-0 h-10 rounded-lg border-[#0E5B52]/15 bg-white text-[13px] font-semibold text-[#35534E] shadow-none hover:bg-[#F0F6F1] hover:text-[#0F2A26]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-admin-logout"
              disabled={isSigningOut}
              onClick={(event) => {
                event.preventDefault();
                void handleLogout();
              }}
              className="h-10 rounded-lg bg-red-600 text-[13px] font-semibold text-white shadow-none hover:bg-red-700 focus-visible:ring-red-500"
            >
              {isSigningOut ? (
                <>
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                  Signing out…
                </>
              ) : (
                "Sign out"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
}
